import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
// Import Shared Components
import { Input } from 'shared-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared Validations
import {
  validateUsername,
  validateLoginPassword,
} from 'shared-components/src/validations/cardholderValidations';
// Import Gobal Components
import InputGroup from '../../../layouts/InputGroup';
// Import Local Components
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
import PwdEyeButton from './components/PwdEyeButton';
// Import Styles
import { PasswordFieldWrapper } from '../styles';
// Import Utils
import { login } from '../../../utils/auth';

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const page = 'auth';

  // Set States
  const initialUserInfo = {
    username: '',
    password: '',
  };
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [passwordType, setPasswordType] = useState('password');
  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [userInfo, setUserInfo] = useUpdateStateObject(initialUserInfo);

  useEffect(() => {
    // Handle error messages from redirects (e.g., MFA failures)
    if (location.state && location.state.error) {
      const errorMsg = location.state.error;
      setFormError(errorMsg);

      // Clear the error from location state to prevent it from persisting
      window.history.replaceState({}, document.title);
    }

    // Handle success messages
    if (location.state && location.state.success) {
      setSuccess(location.state.success);
      // Remove success message from state

      const clearSuccess = setTimeout(() => {
        // Clear Success message after 5 seconds
        setSuccess(null);
      }, 5000);

      return () => {
        setSuccess(null);
        clearTimeout(clearSuccess);
      };
    }

    // Handle redirect from CAMS for already registered users
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('from') === 'cams') {
      setSuccess('cams-already-registered');
      // Clear the query param from URL
      window.history.replaceState({}, document.title, '/login');
    }

    return () => setSuccess(null);
  }, [location.state, location.search]);

  // eslint-disable-next-line consistent-return
  async function handleLogin(username, password, setLoading, setFormError) {
    if (!validateUsername(username))
      return setFormError('invalid-username-format-error');
    if (!validateLoginPassword(password))
      return setFormError('invalid-password-format-error');

    try {
      setLoading(true);
      setFormError(false);
      await login(username, password);
    } catch (error) {
      setLoading(false);
      // Handle new error format with dynamic values
      if (error.details && (error.message === 'account_locked' || error.message === 'invalid_credentials')) {
        // Only handle these two specific error codes with custom messages
        if (error.message === 'account_locked') {
          // Extract time from message like "Try again in 10 minutes"
          const timeMatch = error.details.match(/Try again in (.+)$/);
          const time = timeMatch ? timeMatch[1] : '10 minutes';
          setFormError({ id: error.message, values: { time } });
        } else if (error.message === 'invalid_credentials') {
          // Extract attempts from message like "9 attempts remaining"
          const attemptsMatch = error.details.match(/(\d+) attempts remaining/);
          const attempts = attemptsMatch ? attemptsMatch[1] : '0';
          setFormError({ id: error.message, values: { attempts } });
        }
      } else {
        // For all other errors, use the default login failed message
        setFormError('login-failed-error');
      }
    }
  }

  // Set Handlers
  const handleSubmit = async e => {
    e.preventDefault();
    await handleLogin(
      userInfo.username,
      userInfo.password,
      setLoading,
      setFormError
    );
  };

  const handleLoginKeyUp = async e => {
    if (e.keyCode === 13)
      await handleLogin(
        userInfo.username,
        userInfo.password,
        setLoading,
        setFormError
      );
  };

  return (
    <AuthLayout
      page="login"
      heading={intl.messages['login-heading']}
      subHeaderOne={intl.messages['login-subheader1-text']}
      subHeaderTwo={intl.messages['login-subheader2-text']}
      subHeaderThree={intl.messages['login-subheader3-text']}
      subHeaderFour={intl.messages['login-subheader4-text']}
      // subHeaderFive={intl.messages['login-subheader5-text']}
      subHeaderForgotUsername={
        intl.messages['login-subheaderForgotUsername-text']
      }
      error={formError}
      success={success}
    >
      <AuthForm
        page="login"
        onSubmit={handleSubmit}
        onKeyUp={handleLoginKeyUp}
        loading={loading}
        secondaryButtonText={intl.messages['login-auth-button']}
        secondaryButtonFunc={() => {
          setFormError(null);
          return navigate('/forgot-password');
        }}
      >
        <InputGroup
          label={intl.messages[`${page}-username-label`]}
          labelFor="username"
          page={page}
          required
        >
          <Input
            name="username"
            autoComplete="username"
            data-testid="username"
            value={userInfo.username}
            onChange={e =>
              setUserInfo({ key: 'username', value: e.target.value })}
            minLength={3}
            maxLength={30}
            required
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-password-label`]}
          labelFor="password"
          page={page}
          required
        >
          <PasswordFieldWrapper>
            <Input
              name="password"
              autoComplete="current-password"
              data-testid="password"
              value={userInfo.password}
              onChange={e =>
                setUserInfo({ key: 'password', value: e.target.value })}
              type={passwordType}
              minLength={8}
              required
            />
            <PwdEyeButton type={passwordType} settype={setPasswordType} />
          </PasswordFieldWrapper>
        </InputGroup>
      </AuthForm>
    </AuthLayout>
  );
};

export default Login;
